import React, { FC, ReactNode, useState } from 'react';
import cn from 'classnames/bind';
import { Picture, createPictureFactory } from '@funtech-int/yango-shared';
import { IntlShape, useIntl } from 'react-intl';

import { Text, TextSize, TextWeight, Theme } from '@/shared/components/Text';
import { transformAgeRestrictionToRtl } from '@/scenarios/legacy-player/lib/transform-age-restriction';
import { transformImage } from '@/shared/lib/images';

import { transformTvProgramTypeNameMap } from '../../../channel-program/transformers/transform-tv-program-type-name-map';
import { getCurrentProgram } from '../../../channel-program/utils/get-current-program';
import { useReadChannelPrograms } from '../../hooks/use-read-channel-programs-with-ads';
import { useReadChannelPlayerBaseInfo } from '../../hooks/use-read-channel-player-base-info';

import styles from './ChannelPlayerMeta.module.css';

const getChannelTitle = (title?: string | null, episodeTitle?: string | null) =>
    [title, episodeTitle].filter(Boolean).join('. ');

const cx = cn.bind(styles);

const LONG_TITLE_THRESHOLD = 60;

interface FormatProgramDescriptionParams {
    intl: IntlShape;
    programType: string | undefined;
    ageRestriction: string | undefined;
}

const formatProgramDescription = ({
    intl,
    programType,
    ageRestriction,
}: FormatProgramDescriptionParams) =>
    intl.formatMessage(
        {
            id: 'features.channel-player.4f045340353d850895f5dd8b44c4667b',
            defaultMessage:
                '{programType}{ageRestriction, select, undefined {} other {{programType, select, undefined {} other { · }}}}{ageRestriction}',
            description: 'Детали телепрограммы в формате Тип программы · Возрастное ограничение',
        },
        {
            programType,
            ageRestriction: transformAgeRestrictionToRtl(ageRestriction),
        }
    );

const useChannelPlayerMeta = (contentId: string) => {
    const intl = useIntl();
    const baseInfo = useReadChannelPlayerBaseInfo(contentId);
    const programs = useReadChannelPrograms(contentId);
    const program = getCurrentProgram(programs);

    return {
        channelName: baseInfo?.title || '',
        channelLogoUrl: transformImage(baseInfo?.gallery.logos.main),
        programTitle: getChannelTitle(program?.title, program?.episodeTitle),
        programType: transformTvProgramTypeNameMap(intl, program?.typeName),
        ageRestriction: program?.ageRestriction,
    };
};

const pictureFactory = createPictureFactory({
    fallback: {
        srcSet: {
            '1x': '48x48',
            '2x': '96x96',
        },
        src: '48x48',
    },
});

interface ChannelPlayerMetaProps {
    contentId: string;
    className?: string;
    children: (props: { onEnlargementChanged?: (isEnlarged: boolean) => void }) => ReactNode;
}

export const ChannelPlayerMeta: FC<ChannelPlayerMetaProps> = ({
    contentId,
    className,
    children,
}) => {
    const intl = useIntl();

    const { channelName, channelLogoUrl, programTitle, programType, ageRestriction } =
        useChannelPlayerMeta(contentId);

    const { img } = pictureFactory({
        fallbackUrl: channelLogoUrl,
    });

    const programDescription = formatProgramDescription({ intl, programType, ageRestriction });

    const [isMetaHidden, setIsMetaHidden] = useState(false);

    const isLongTitle = programTitle.length > LONG_TITLE_THRESHOLD;

    return (
        <section className={cx('root', className)}>
            <div className={cx('channel', { hidden: isMetaHidden })}>
                <Picture
                    className={cx('channel-icon')}
                    alt=""
                    loading="lazy"
                    srcSet={img.srcSet}
                    src={img.src}
                    data-tid="AdaptiveImage"
                />
                <Text
                    className={cx('channel-name')}
                    theme={Theme.Dark}
                    weight={TextWeight.Bold}
                    size={TextSize.Title}
                >
                    {channelName}
                </Text>
            </div>
            <Text
                className={cx('title', {
                    title_long: isLongTitle,
                    hidden: isMetaHidden,
                })}
                weight={TextWeight.Bold}
                size={TextSize.Superhead}
                theme={Theme.Dark}
            >
                {programTitle}
            </Text>
            <div className={cx('bottomControls', { hidden: isMetaHidden })}>
                <div className={cx('meta')}>
                    <Text
                        className={cx('description')}
                        theme={Theme.Dark}
                        weight={TextWeight.Medium}
                        size={TextSize.Subtitle}
                    >
                        {programDescription}
                    </Text>
                </div>
            </div>
            {children({ onEnlargementChanged: (isEnlarged) => setIsMetaHidden(isEnlarged) })}
        </section>
    );
};
